import React, { useCallback } from 'react';
import { Options } from '@splidejs/splide';
import '@splidejs/splide/dist/css/themes/splide-default.min.css';

import ProductsCarousel, {
  ProductsCarouselProps,
} from './ProductsCarousel/ProductsCarousel';
import ProductCardContainer from 'components/Common/Card/ProductCard/ProductCardContainer/ProductCardContainer';
import ProductCardImage from 'components/Common/Card/ProductCard/ProductCardImage/ProductCardImage';
import ProductCardDiscount from 'components/Common/Card/ProductCard/ProductCardDiscount/ProductCardDiscount';
import ProductCardStickers from 'components/Common/Card/ProductCard/ProductCardStickers/ProductCardStickers';
import ProductCardDetails from 'components/Common/Card/ProductCard/ProductCardDetails/ProductCardDetails';
import ProductCardLabel from 'components/Common/Card/ProductCard/ProductCardLabel/ProductCardLabel';
import ProductCardPrice from 'components/Common/Card/ProductCard/ProductCardPrice/ProductCardPrice';
import { getDataFromAttributes } from 'services/data';
import { getProductData } from 'services/gtm/gtmPageView';
import { formatProduct } from 'services/gtm/gtmUtils';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { ProductDetail } from 'types/Controls/ProductSheet';

import styles from './CarouselProducts.module.scss';

type Props = {
  /** List of products */
  products?: CarouselProductType[];
  /** List of products details */
  productsDetails?: ProductDetail[];
  /** Products attributes return by Octipas API */
  attributes?: { [key: string]: { Label: string; MainName: string } };
  /** Function onClose in search mobile */
  closeAcp?: () => void;
  /** Function when product is clicked */
  onClickProduct?: () => void;
  /** gtm element label */
  gtmElementLabel?: string;
  /** Override / Additional options to splide component */
  splideOptions?: Options;
  title?: string;
  classes?: {
    carousel?: ProductsCarouselProps<CarouselProductType>['classes'];
  };
};

/**
 * Display a list of products in carousel
 */
export function CarouselProducts(props: Props): JSX.Element | null {
  const {
    products,
    productsDetails,
    attributes,
    closeAcp,
    onClickProduct,
    gtmElementLabel,
    splideOptions,
    title,
    classes,
  } = props;

  const handleClick = useCallback(
    (_product: CarouselProductType, index: number) => {
      scrollTo(0, 0);
      if (productsDetails) {
        const productDataListing = getDataFromAttributes(
          productsDetails[index].Attributes,
          attributes || {}
        );

        pushGtmEvent({
          event: '_product_click',
          category: 'recommandation de produit',
          element: gtmElementLabel,
          products: [
            {
              ...getProductData(formatProduct(productDataListing)),
              list_position: index + 1,
            },
          ],
        });
      }

      closeAcp?.();
      onClickProduct?.();
    },
    []
  );

  if (!products?.length) {
    return null;
  }

  return (
    <ProductsCarousel
      title={title}
      products={products}
      renderProduct={(product, index) => (
        <Product
          product={product}
          onClick={() => handleClick(product, index)}
        />
      )}
      splideOptions={splideOptions}
      classes={classes?.carousel}
    />
  );
}

function Product({
  product,
  onClick,
}: {
  product: CarouselProductType;
  onClick?: () => void;
}) {
  return (
    <ProductCardContainer
      linkTo={product.url}
      onClick={onClick}
      className={styles.card}
    >
      <ProductCardImage
        images={[{ alt: product.shortLabel, src: product.visuelFace }]}
      >
        {product.discountPercentage &&
          parseInt(product.discountPercentage) > 0 && (
            <ProductCardDiscount
              discountPercentage={product.discountPercentage}
            />
          )}
        <ProductCardStickers
          opecoSticker={product.commercialOperationSticker}
          classes={{ sticker: styles.sticker }}
        />
      </ProductCardImage>
      <ProductCardDetails className={styles.details}>
        <ProductCardLabel
          brand={product.metaMarque}
          label={product.shortLabel}
          className={styles.labelContainer}
        />
        <ProductCardPrice
          discountPrice={product.discountPrice}
          price={product.price}
          lotQuantity={product.lotQuantity}
          classes={{ priceFrom: styles.priceFrom }}
        />
      </ProductCardDetails>
    </ProductCardContainer>
  );
}

export type CarouselProductType = {
  productId: number;
  visuelFace: string;
  price: string;
  discountPrice?: string;
  uniquePrice: string;
  shortLabel: string;
  url: string;
  metaMarque?: string;
  lotQuantity?: number;
  discountPercentage?: string;
  commercialOperationSticker?: string;
};
